import type { Survey } from '@responsedock/types';
import { createEmbedLifecycleEvent } from '../../../../packages/survey-core/src/embed-protocol';
import { useAuthStore } from '../store/auth';

type SurveySummary = Pick<Survey, 'id' | 'slug' | 'title' | 'status'>;

type EmbedMessage =
  | { type: 'responsedock.survey.created'; survey: SurveySummary; templateId?: string | null; sourceSurveyId?: string | null }
  | { type: 'responsedock.survey.published'; surveyId: string }
  | { type: 'responsedock.survey.opened'; surveyId: string; slug: string }
  | { type: 'responsedock.survey.ready'; surveyId: string }
  | { type: 'responsedock.survey.completed'; surveyId: string; responseId?: string | null }
  /** templateId / sourceSurveyId say where a new questionnaire came from (template, duplicate). */
  | { type: 'responsedock.questionnaire.created'; questionnaire: SurveySummary; templateId?: string | null; sourceSurveyId?: string | null }
  | { type: 'responsedock.questionnaire.saved'; surveyId: string }
  | { type: 'responsedock.questionnaire.published'; surveyId: string }
  | { type: 'responsedock.questionnaire.selected'; surveyId: string; slug: string }
  | { type: 'responsedock.questionnaire.share_requested'; questionnaire: SurveySummary };

export function postEmbedMessage(message: EmbedMessage) {
  const { isEmbedded, embedContext } = useAuthStore.getState();
  if (!isEmbedded || !embedContext?.allowedOrigin || !window.parent || window.parent === window) return;

  const envelope = {
    source: 'responsedock',
    workspaceId: embedContext?.workspaceId ?? null,
    tenant: embedContext?.tenant ?? null,
    subject: embedContext?.subject ?? null,
    ...message,
  };
  window.parent.postMessage(envelope, embedContext.allowedOrigin);

  const legacyMessage = message.type === 'responsedock.questionnaire.created'
    ? { ...envelope, type: 'responsedock.survey.created', survey: message.questionnaire }
    : message.type === 'responsedock.questionnaire.published'
      ? { ...envelope, type: 'responsedock.survey.published' }
      : message.type === 'responsedock.questionnaire.selected'
        ? { ...envelope, type: 'responsedock.survey.opened' }
        : null;
  if (legacyMessage) window.parent.postMessage(legacyMessage, embedContext.allowedOrigin);

  if (message.type === 'responsedock.survey.completed' && embedContext?.allowedOrigin) {
    window.parent.postMessage(createEmbedLifecycleEvent(
      'responsedock.lifecycle.completed',
      { responseId: message.responseId },
      {
        frame: embedContext.role === 'respondent' ? 'respondent-dashboard' : 'admin',
        workspaceId: embedContext.workspaceId,
        surveyId: message.surveyId,
      },
    ), embedContext.allowedOrigin);
  }
}

/**
 * Ask the host page to bring `top` px below this frame's top edge into view.
 * Returns false when there is no host to ask (not embedded, or not framed).
 */
export function postEmbedScrollRequest(top: number): boolean {
  const { isEmbedded, embedContext } = useAuthStore.getState();
  if (!isEmbedded || !embedContext?.allowedOrigin || !window.parent || window.parent === window) return false;
  const safeTop = Number.isFinite(top) ? Math.max(0, Math.round(top)) : 0;
  window.parent.postMessage(createEmbedLifecycleEvent(
    'responsedock.lifecycle.scroll_request',
    { top: safeTop },
    {
      frame: embedContext.role === 'respondent' ? 'respondent-dashboard' : 'admin',
      workspaceId: embedContext.workspaceId,
      surveyId: embedContext.surveyId ?? null,
    },
  ), embedContext.allowedOrigin);
  return true;
}
